/**
 * The bank-picker form for the e-laskuehdotus request (048).
 *
 * Split out of `EInvoiceProposalRequest.tsx` when that file crossed the enforced
 * 400-line limit. The seam is not arbitrary: everything here is PRESENTATION
 * given state and handlers, while the parent keeps the state machine — the
 * submit intent, the idempotency key bound to it, and the outcome branches.
 * Nothing in this file decides anything.
 *
 * `selectRef` is passed explicitly rather than forwarded, because the parent
 * focuses the picker after a missing-bank submit and needs the handle to a node
 * this component owns.
 *
 * Owned by feature 048-member-einvoice-request.
 */

import type { JSX, RefObject } from 'react';

import { useTranslate } from '../../i18n/useTranslate.js';
import type { SelectableBank } from '../../membership-billing/MembershipBillingPort.js';
import { paragraphs } from './einvoiceProposalHelpers.js';

export interface EInvoiceBankPickerProps {
  readonly formId: string;
  readonly selectId: string;
  readonly bankRequiredId: string;
  readonly selectRef: RefObject<HTMLSelectElement | null>;
  readonly banks: readonly SelectableBank[];
  readonly selectedBankId: string;
  /** A request is in flight: every control refuses, none of them disappear. */
  readonly sending: boolean;
  readonly showBankRequired: boolean;
  /** The register rejected the bank still selected here. */
  readonly bankRefused: boolean;
  /** Whichever error currently explains the picker, if any. */
  readonly describedBy: string | undefined;
  readonly onSelect: (bankId: string) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

export function EInvoiceBankPicker({
  formId,
  selectId,
  bankRequiredId,
  selectRef,
  banks,
  selectedBankId,
  sending,
  showBankRequired,
  bankRefused,
  describedBy,
  onSelect,
  onCancel,
  onSubmit,
}: EInvoiceBankPickerProps): JSX.Element {
  const t = useTranslate();

  return (
    <div id={formId} className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <h4 className="text-base font-semibold text-foreground">
        {t('profile.fee.eInvoice.request')}
      </h4>
      {paragraphs(t('profile.fee.eInvoice.description')).map((text, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a static split of one copy string
        <p key={index} className="text-sm text-muted-foreground">
          {text}
        </p>
      ))}

      <label className="flex flex-col gap-1 text-sm font-medium text-foreground" htmlFor={selectId}>
        {t('profile.fee.eInvoice.selectBank')}
        <select
          id={selectId}
          ref={selectRef}
          value={selectedBankId}
          aria-required="true"
          aria-disabled={sending}
          aria-invalid={showBankRequired || bankRefused}
          aria-describedby={describedBy}
          onChange={(event) => {
            // Changing the bank mid-flight would mint a new key and re-enable
            // submit — a second proposal before the first settles.
            if (sending) return;
            onSelect(event.target.value);
          }}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('profile.fee.eInvoice.placeholder')}</option>
          {banks.map((bank) => (
            <option key={bank.id} value={String(bank.id)}>
              {bank.name}
            </option>
          ))}
        </select>
      </label>
      {showBankRequired && (
        <p id={bankRequiredId} className="text-sm text-destructive">
          {t('profile.fee.eInvoice.placeholder')}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            if (sending) return;
            onCancel();
          }}
          aria-disabled={sending}
          className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          {t('profile.fee.eInvoice.cancel')}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          // aria-disabled rather than `disabled`: a native disabled control that
          // takes focus drops it to <body>. Only while sending: with no bank
          // chosen the button still acts (it explains what is missing and moves
          // focus to the picker), so announcing it as disabled would be false.
          aria-disabled={sending}
          aria-busy={sending}
          className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          {sending ? t('profile.fee.eInvoice.submitting') : t('profile.fee.eInvoice.submit')}
        </button>
      </div>
    </div>
  );
}
